import React, {
  BaseSyntheticEvent,
  createContext,
  FC,
  PropsWithChildren,
  Ref,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { FieldValues, UseFieldArrayReturn } from 'react-hook-form';
import { Schema, ZodTypeDef } from 'zod/v3';

/**
 * Special symbol to indicate that a form submission was not committed, for various reasons (e.g., validation failure, user cancellation).
 * This allows the form to avoid following certain post-submission actions, such as resetting the form state.
 **/
export const FORM_SUBMIT_NOT_COMMITTED = Symbol('FORM_SUBMIT_NOT_COMMITTED');

export type FormSubmitHandler<FormData extends FieldValues = any> = (
  data: FormData,
  confirmed: boolean,
  event?: BaseSyntheticEvent,
) => unknown | Promise<unknown>;

export type FieldArrayRef = Ref<
  (name: string) => UseFieldArrayReturn<any, any, any>
>;

interface FieldArrays {
  [name: string]: UseFieldArrayReturn<any, any, any>;
}

interface AdditionalFormContextValue<FormData extends FieldValues = any> {
  schema: Schema<FormData, ZodTypeDef, any>;
  onSubmit: FormSubmitHandler<FormData>;
  idAttributePrefix: string;
  getFieldArray: (name: string) => UseFieldArrayReturn<any, any, any>;
  addFieldArray: (
    name: string,
    fieldArray: UseFieldArrayReturn<any, any, any>,
  ) => void;
  removeFieldArray: (name: string) => void;
  onChange?: () => void;
}

const AdditionalFormContext = createContext<AdditionalFormContextValue>(
  undefined!,
);

export const AdditionalFormContextProvider: FC<
  PropsWithChildren<
    Pick<
      AdditionalFormContextValue,
      'schema' | 'onSubmit' | 'idAttributePrefix' | 'onChange'
    > & {
      fieldArrayRef?: FieldArrayRef;
    }
  >
> = ({ children, ...rest }) => {
  const fieldArraysCache = useRef<FieldArrays>({});
  const getFieldArray = useCallback(
    (name: string) => fieldArraysCache.current[name],
    [fieldArraysCache],
  );
  const addFieldArray = useCallback(
    (name: string, fieldArray: UseFieldArrayReturn<any, any, any>) => {
      fieldArraysCache.current[name] = fieldArray;
    },
    [fieldArraysCache],
  );
  const removeFieldArray = useCallback(
    (name: string) => {
      delete fieldArraysCache.current[name];
    },
    [fieldArraysCache],
  );

  const value = useMemo(
    () => ({ ...rest, getFieldArray, addFieldArray, removeFieldArray }),
    [rest, getFieldArray, addFieldArray, removeFieldArray],
  );

  useImperativeHandle(rest.fieldArrayRef, () => getFieldArray, [getFieldArray]);

  return (
    <AdditionalFormContext.Provider value={value}>
      {children}
    </AdditionalFormContext.Provider>
  );
};

export const useAdditionalFormContext = <
  FormData extends FieldValues = FieldValues,
>() => {
  const context = useContext(
    AdditionalFormContext,
  ) as AdditionalFormContextValue<FormData>;

  if (!context) {
    throw new Error(
      'useAdditionalFormContext must be used within AdditionalFormContextProvider',
    );
  }

  return context;
};

export const useFormSubmitHandler = <
  FormData extends FieldValues = FieldValues,
>() => {
  const { onSubmit } = useAdditionalFormContext<FormData>();
  return onSubmit;
};

export const useFormSchema = <FormData extends FieldValues = FieldValues>() => {
  const { schema } = useAdditionalFormContext<FormData>();
  return schema;
};

export const useFormIdAttributePrefix = <
  FormData extends FieldValues = FieldValues,
>() => {
  try {
    /* eslint-disable-next-line react-hooks/rules-of-hooks */
    const { idAttributePrefix } = useAdditionalFormContext<FormData>();
    return idAttributePrefix;
  } catch (_e) {
    return '';
  }
};

export const useFormFieldArray = (name: string) => {
  const { getFieldArray } = useAdditionalFormContext();
  const fieldArray = getFieldArray(name);
  if (!fieldArray) {
    throw new Error(`Field array "${name}" not found`);
  }
  return fieldArray;
};

export const useAddFormFieldArray = () => {
  const { addFieldArray } = useAdditionalFormContext();
  return useCallback(
    (name: string, fieldArray: UseFieldArrayReturn<any, any, any>) =>
      addFieldArray(name, fieldArray),
    [addFieldArray],
  );
};

export const useRemoveFormFieldArray = () => {
  const { removeFieldArray } = useAdditionalFormContext();
  return useCallback(
    (name: string) => removeFieldArray(name),
    [removeFieldArray],
  );
};
